import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";

import { Helmet } from "react-helmet";
import client from "@culturehq/client";
import { classnames, Icon, Loader, Tooltip } from "@culturehq/components";

import { useFetch, useFieldsLabels, useInsideIframe, useOrganizationTitle, useWindowDimensions } from "app/hooks";
import { CompanyLink, DepartmentLink, ProgramLink, DegreeLink, LocationLink } from "app/links";
import { canViewSkillsField } from "app/permissions";
import { displayLastName, getName, isMicrosoft, isPlatformSubdomain, isSynopsys, t, Trans } from "app/utils";

import LandingPageStories from "components/admin/LandingPageStories";

import { TimeDiffYears, getTimeDiff } from "components/common/Time";
import Tags from "components/person/Tags";
import AccessibleFocusOutlineElement from "components/common/AccessibleFocusOutlineElement";
import ErrorPage from "components/ErrorPage";
import ShareLinkComponent from "components/common/ShareLinkComponent";
import getUserName from "app/utils/getUserName";

const DEFAULT_COLOR = "#79b17d";

const LandingPageUserCallout = ({ orgName }) => {
  const { pathname } = useLocation();
  const { userId } = useParams();
  const organization = orgName.split("-").join(" ");
  const [showUserInfo, setShowUserInfo] = useState(true);
  const { _, width } = useWindowDimensions();
  const insideIframe = useInsideIframe();
  const { updateSeoTitle } = useOrganizationTitle();
  const isSelectedOrgs = isMicrosoft(orgName) || isSynopsys(orgName);

  const { error, fetching, json: {
    landingPage, user
  } } = useFetch(
    () => client.listOrganizationLandingPages({
      organization: isPlatformSubdomain(organization) ? undefined : organization,
      userId
    }),
    [organization]
  );

  const labels = useFieldsLabels(landingPage?.organization);

  useEffect(() => updateSeoTitle(organization), [organization, updateSeoTitle]);

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (fetching) {
    return <Loader loading />;
  }

  const PreviewHeaderTextStyle = isSelectedOrgs ? {}
    : { backgroundImage: `linear-gradient(120deg, ${landingPage?.primaryColor || DEFAULT_COLOR} 0%, ${landingPage?.primaryColor || DEFAULT_COLOR}38 100%)` };

  return (
    <div className={classnames("LandingPageCallout", "LandingPageUserCallout", orgName)}>
      <Helmet>
        <meta name="description" content={`${getName(user)}'s stories`} />
        <meta property="og:title" content={`Meet ${getName(user)}`} />
        <meta property="og:description" content={`${getName(user)}'s stories`} />
        <meta property="og:image" content={user.avatar.mediumUrl} />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <div className="LandingPageCallout--Preview">
        <div className="Preview">
          <div className="Preview-Header__Image" style={{ backgroundImage: `url(${landingPage.headerImageUrl}` }}>
            <div className="Preview-Header__Overlay">
              <h1 className="Preview-Header__Text" style={PreviewHeaderTextStyle}>
                <Trans>Meet {getName(user)}</Trans>
              </h1>
            </div>
            {!insideIframe && landingPage?.organization && (
              <ShareLinkComponent
                link={`https://${orgName.replace(/\s+/g, "-").toLowerCase()}.culturehq.com${pathname || ""}`}
                organizationId={landingPage.organization.id}
                orgName={orgName}
              />
            )}
          </div>
          <div className="Preview-Body row">
            <div className="col-md-3 Preview-LeftColumn view-sec">
              <Link className="back-arrow" to="/">
                <Icon icon="arrow-left-c" />
                <Trans>Go Back</Trans>
              </Link>
              <img className="Preview-Logo" src={user.avatar.mediumUrl} alt={t("User photo")} />
              <h1 className="Person--name">{getUserName(user, displayLastName(orgName))}</h1>
              {landingPage.organization.isEducational ? (
                <h2 className="Person--title">{user.biography}</h2>
              ) : (
                <h2 className="Person--title">{user.title}</h2>
              )}
              <ul className="e_label show-md">
                <li className={showUserInfo ? "selected" : ""} onClick={() => setShowUserInfo(true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    <Trans>About</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
                <li className={!showUserInfo ? "selected" : ""} onClick={() => setShowUserInfo(false)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    <Trans>Stories</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
              </ul>
              {(showUserInfo || width > 992) && (
                <div className="Preview-Tags">
                  <div className="Section__title">
                    <h2 className="subtitle" style={isSelectedOrgs ? {} : { color: landingPage?.primaryColor }}><Trans>highlights</Trans></h2>
                  </div>
                  <div className="Section__highlights">
                    {user.startedOn && (
                      <p aria-label={t(`Time in the company: ${getTimeDiff(user.startedOn)}`)}>
                        <Tooltip tip={t("Tenure")}>
                          <Icon icon="clock" />
                        </Tooltip>
                        <TimeDiffYears time={user.startedOn} />
                      </p>
                    )}
                    {user.departments.length > 0 && (
                      <p aria-label={t(labels.departments.plural)}>
                        <Tooltip tip={t(labels.departments.plural)}>
                          <Icon icon="ios-people-outline" />
                        </Tooltip>
                        <span>
                          {user.departments.map((department, index) => (
                            <span key={department.id}>
                              <DepartmentLink department={department} />
                              {index !== user.departments.length - 1 && ", "}
                            </span>
                          ))}
                        </span>
                      </p>
                    )}
                    {user.companies.length > 0 && (
                      <p aria-label={t(labels.companies.plural)}>
                        <Tooltip tip={t(labels.companies.plural)}>
                          <Icon icon="building" />
                        </Tooltip>
                        <span>
                          {user.companies.map((company, index) => (
                            <span key={company.id}>
                              <CompanyLink company={company} />
                              {index !== user.companies.length - 1 && ", "}
                            </span>
                          ))}
                        </span>
                      </p>
                    )}
                    {user.location && (
                      <p aria-label={t(`Location:  ${user.location.name}`)}>
                        <Tooltip tip={t("Location")}>
                          <Icon icon="loc-ol" />
                        </Tooltip>
                        <LocationLink location={user.location} />
                      </p>
                    )}
                    {user.degree && (<p aria-label={t(`Degree:  ${user.degree}`)}><Icon icon="mortarboard" /><DegreeLink degree={user.degree} /></p>)}
                    {user.programs?.length > 0 && (
                      <p aria-label={t("Programs:")}>
                        <Icon icon="ios-star-outline" />
                        <span>
                          {user.programs.map((program, index) => (
                            <span key={program.id}>
                              <ProgramLink program={program} />
                              {index !== user.programs.length - 1 && ", "}
                            </span>
                          ))}
                        </span>
                      </p>
                    )}
                  </div>
                  {user.interests.length !== 0 && (
                    <Tags
                      color={landingPage?.primaryColor}
                      icon="heart-ol"
                      iconCommonality="heart"
                      label="interests"
                      myTags={[]}
                      userTags={user.interests}
                      landingPage
                      orgName={orgName}
                    />
                  )}
                  {user.skills.length !== 0 && canViewSkillsField(organization) && (
                    <Tags
                      color={landingPage?.primaryColor}
                      icon="hammer"
                      iconCommonality="hammer-filled"
                      label="SKILLS"
                      myTags={[]}
                      userTags={user.skills}
                      landingPage
                      orgName={orgName}
                    />
                  )}
                </div>
              )}
            </div>
            {(!showUserInfo || width > 992) && (
              <div className="col-md-9">
                <div className="Preview-Browser__Stories">
                  <h2><Trans>{getName(user)}&apos;s stories</Trans></h2>
                  <LandingPageStories
                    hideCreator
                    insideIframe={insideIframe}
                    organizationId={landingPage.organization.id}
                    organization={organization}
                    orgName={orgName}
                    user={user}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageUserCallout;
